/**
 * PaperRelations — geometric relations between the paper quad and the hand
 * / a detected phone, in normalized [0,1] image coordinates. Observation
 * only: reports overlap / proximity, no intent.
 *
 * Surfaces the "core info" requested in review (2026-06-06): is the hand
 * covering the paper, is a phone near the paper. The paper's position,
 * visibility (PaperClip.visibleFraction) and gaze-in/out (PaperZoneState)
 * already exist elsewhere; these two relations were the missing pieces.
 */
import type { Point2D } from './Homography';

/** Ray-casting point-in-polygon for a (convex or concave) quad. */
export function pointInQuad(px: number, py: number, quad: Point2D[]): boolean {
  if (quad.length < 3) return false;
  let inside = false;
  for (let i = 0, j = quad.length - 1; i < quad.length; j = i++) {
    const xi = quad[i].x, yi = quad[i].y, xj = quad[j].x, yj = quad[j].y;
    const intersect = (yi > py) !== (yj > py)
      && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export interface HandPaperRelation {
  /** Fraction of hand landmarks inside the paper quad [0,1]. */
  coverage: number;
  /** coverage ≥ OVER_THRESHOLD — hand is over the paper region. */
  over: boolean;
}

const OVER_THRESHOLD = 0.3;

/** How much of the hand sits over the paper quad. */
export function handOverPaper(
  landmarks: Array<{ x: number; y: number }> | null,
  quad: Point2D[],
): HandPaperRelation {
  if (!landmarks || landmarks.length === 0 || quad.length < 3) {
    return { coverage: 0, over: false };
  }
  let inside = 0;
  for (const lm of landmarks) if (pointInQuad(lm.x, lm.y, quad)) inside++;
  const coverage = inside / landmarks.length;
  return { coverage, over: coverage >= OVER_THRESHOLD };
}

export interface PhonePaperRelation {
  /** Phone bbox overlaps the paper's bounding box. */
  overlap: boolean;
  /** Normalized gap between the two boxes (0 when overlapping). */
  gapNorm: number;
  /** overlap OR gap < NEAR_THRESHOLD. */
  near: boolean;
}

const NEAR_THRESHOLD = 0.12; // ~12% of the frame diagonal

/** Whether a phone bbox is over / near the paper quad. */
export function phoneNearPaper(
  bbox: { x: number; y: number; w: number; h: number } | null,
  quad: Point2D[],
): PhonePaperRelation {
  if (!bbox || quad.length < 3) return { overlap: false, gapNorm: 1, near: false };
  const xs = quad.map(p => p.x), ys = quad.map(p => p.y);
  const pMinX = Math.min(...xs), pMaxX = Math.max(...xs);
  const pMinY = Math.min(...ys), pMaxY = Math.max(...ys);
  const bX0 = bbox.x, bY0 = bbox.y, bX1 = bbox.x + bbox.w, bY1 = bbox.y + bbox.h;
  const overlap = bX0 < pMaxX && bX1 > pMinX && bY0 < pMaxY && bY1 > pMinY;
  const dx = Math.max(0, pMinX - bX1, bX0 - pMaxX);
  const dy = Math.max(0, pMinY - bY1, bY0 - pMaxY);
  const gapNorm = Math.hypot(dx, dy);
  return { overlap, gapNorm, near: overlap || gapNorm < NEAR_THRESHOLD };
}
